import { create } from "zustand";

/**
 * useToastStore — H5 `showToast(message)` equivalent.
 *
 * H5 source: main.js:9907-9914 — sets `els.toast.textContent`, adds
 * `.toast.is-visible`, auto-clears after 1800ms via setTimeout.
 *
 * React surface: any module can call
 *   useToastStore.getState().show("已关注晚灯。")
 * to trigger the global toast. `<ToastHost />` (mounted in AppShell)
 * subscribes to this store and renders the H5 `.toast` element with
 * `role="status"`, so React Testing Library assertions like
 *   findByRole("status")
 * keep working after pages migrate from inline `<p role="status">` to
 * the global toast.
 */

const TOAST_DURATION_MS = 1800;

/**
 * Toast severity (Audit C, docs/plans/error-empty-loading-audit-2026-05-20.md):
 *   - "polite": informational / success — announced by SR after current
 *     utterance finishes. Maps to role="status" + aria-live="polite".
 *   - "assertive": error / warning — interrupts current utterance.
 *     Maps to role="alert" + aria-live="assertive".
 *
 * Default for `show()` stays polite so existing call sites are
 * unaffected. Use `showError()` for explicit error toasts.
 */
export type ToastSeverity = "polite" | "assertive";

export type ToastStore = {
  message: string;
  visible: boolean;
  severity: ToastSeverity;
  show: (message: string, severity?: ToastSeverity) => void;
  /**
   * Audit C convenience: show an error toast with severity="assertive".
   * Equivalent to `show(message, "assertive")` but reads better at call
   * sites and makes log-grep / future telemetry pluggable.
   */
  showError: (message: string) => void;
  hide: () => void;
  /** Test-only: clear any pending auto-hide timer + reset state. */
  reset: () => void;
};

let hideTimer: ReturnType<typeof setTimeout> | null = null;

function clearHideTimer() {
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

export const useToastStore = create<ToastStore>((set, get) => ({
  message: "",
  visible: false,
  severity: "polite",
  show: (message: string, severity: ToastSeverity = "polite") => {
    clearHideTimer();
    set({ message, severity, visible: true });
    hideTimer = setTimeout(() => {
      set({ visible: false });
      hideTimer = null;
    }, TOAST_DURATION_MS);
  },
  showError: (message: string) => {
    get().show(message, "assertive");
  },
  hide: () => {
    clearHideTimer();
    set({ visible: false });
  },
  reset: () => {
    clearHideTimer();
    set({ message: "", severity: "polite", visible: false });
  },
}));
